var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from "react";
import { useObserver } from "../hooks/useObserver";
import { useRenderTrigger } from "../hooks/useRenderTrigger";
import { spinalToCamelCase } from "../utils";
import { getAttributes, getChildren, getUserActionCustomPseudoClassList, isText, } from "../utils/dom";
import { Element } from "./Element";
/**
 * User action events to track and update style
 * https://developer.mozilla.org/en-US/docs/Web/Events#event_listing
 */
const TRACK_EVENTS = new Set([
    "click",
    "input",
    "keypress",
    "keydown",
    "keyup",
    "mousemove",
    "mousedown",
    "mouseup",
    "scroll",
]);
const OBSERVER_INIT = {
    attributes: true,
    childList: true,
    subtree: false,
    characterData: true,
};
export function Reflection({ className, real, style }) {
    const _a = getAttributes(real), { class: classList, style: styleList } = _a, attributes = __rest(_a, ["class", "style"]);
    const children = getChildren(real).map((childNode, i) => {
        return React.createElement(Reflection, { key: i, real: childNode, style: style });
    });
    const pseudoClassList = getUserActionCustomPseudoClassList(real);
    // Trigger a rerender
    const { rerender: onUpdate } = useRenderTrigger();
    // Observe changes on real element
    useObserver({
        initOptions: OBSERVER_INIT,
        ObserverClass: MutationObserver,
        onEvents: TRACK_EVENTS,
        onUpdate,
        target: real,
    });
    if (!real)
        return null;
    if (isText(real))
        return React.createElement(React.Fragment, null, real.textContent);
    return (React.createElement(Element, Object.assign({ className: mergeClassList(className, classList, ...pseudoClassList), readOnly: true, scroll: { x: real.scrollLeft, y: real.scrollTop }, style: mergeStyleProps(style, styleList), tagName: real.nodeName }, attributes), children));
}
function mergeClassList(...cls) {
    return cls.filter(Boolean).join(" ");
}
export function mergeStyleProps(cssProps = {}, inlineCSS = "", styleDecl) {
    return Object.assign(Object.assign(Object.assign({}, (styleDecl &&
        asCSSProperties(Array.from(styleDecl), (prop) => prop, (prop) => styleDecl.getPropertyValue(prop)))), asCSSProperties(inlineCSS.split(";").map((propValue) => propValue.split(":")), ([prop]) => prop.trim(), ([, value]) => value === null || value === void 0 ? void 0 : value.trim())), cssProps);
}
function asCSSProperties(items, getProp, getValue) {
    return Object.fromEntries(items
        .map((item) => [spinalToCamelCase(getProp(item)), getValue(item)])
        .filter(([, value]) => Boolean(value)));
}
